import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { ErrorCodeEnum } from '@ulysses-inc/harami_api_client'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { Redirect, Route } from 'react-router-dom'
import { Spinner } from 'src/components/spinner/Spinner'
import { localStorageKeys } from 'src/constants/localStorageKeys'
import { discardListFiltersOnPlaceNodesChange } from 'src/features/auth/discardListFiltersOnPlaceNodesChange'
import { invalidateFeatureFlagsQuery } from 'src/features/featureFlags/useFeatureFlags'
import { setUserPropertiesFromLocalStorage } from 'src/features/firebase/userProperties'
import { useSetGtmUserId } from 'src/features/gtm/useSetGtmUserId'
import { useSetSentryUser } from 'src/features/sentry/useSetSentryUser'
import {
  AuthError,
  useQueryLoginUserContext,
} from 'src/features/tanStackQuery/commonQueries/useQueryLoginUserContext'

type AuthCheckResult = 'passed' | 'failed' | 'unknown'

type DialogInfo = {
  title: string
  content: string
}

type Props = {
  children: ReactNode
}

/**
 * 認証が必要な画面を初回にマウントした際に以下を行う。
 * - ユーザーが認証済みであることを検証する。次項のAPIを呼び出せることをもって検証に代える。
 * - APIからユーザーコンテキストを取得したうえローカルストレージにセットする。
 *   (従前のコードを踏襲してローカルストレージに保持しているものの、特に意図や効能はないので、
 *    普通にTanStackのクエリを使うよう関係個所をリファクタしたい)
 */
export const AuthGuard = ({ children }: Props) => {
  const queryClient = useQueryClient()
  const [dialogDismissed, setDialogDismissed] = useState(false)

  // 配下の画面にはローカルストレージへの書き込みが完了してからマウントされることを
  // 保証する必要がある（マウント時の effect でログイン系のキーを読むコンポーネントが
  // あるため。例: ChannelTalk が loginUserUuid を読んでユーザー情報を取得する）
  const [storageReady, setStorageReady] = useState(false)

  const { data: loginUserContext, error } = useQueryLoginUserContext({
    noCache: true,
  })

  // ローカルストレージ経由だと AuthGuard 自身のマウント時（書き込み前）に
  // 空・前回ログインユーザーの値を読んでしまうため、取得したコンテキストの値を直接渡す
  useSetGtmUserId(loginUserContext?.userUuid)
  useSetSentryUser(loginUserContext?.userUuid, loginUserContext?.companyId)

  const errorDialogInfo = useMemo((): DialogInfo | null => {
    if (!error) return null
    if (!(error instanceof AuthError)) {
      return {
        title: '不明なエラー',
        content: 'ユーザー情報の取得に失敗したためログイン画面に遷移します。',
      }
    }
    switch (error.code()) {
      case ErrorCodeEnum.CredentialsNotFoundError:
        return null
      case ErrorCodeEnum.ServiceAssignmentRequiredError:
        return { title: '権限不足', content: error.userMessage() }
      case ErrorCodeEnum.InvalidAudienceError:
        return { title: '認証エラー', content: '再ログインが必要です' }
      case ErrorCodeEnum.AccessTokenInactiveError:
        return { title: '認証期限切れ', content: error.userMessage() }
      default:
        return {
          title: '認証期限切れ',
          content: '認証期限が切れたため、再ログインしてください。',
        }
    }
  }, [error])

  useEffect(() => {
    if (!loginUserContext) return

    localStorage.setItem(
      localStorageKeys.loginUserLoginId, // TODO: ID統合が完了したら削除
      loginUserContext.userLoginId,
    )
    localStorage.setItem(
      localStorageKeys.loginUserRumpCustomerMemberId,
      loginUserContext.rumpCustomerMemberId,
    )
    localStorage.setItem(
      localStorageKeys.loginUserUuid,
      loginUserContext.userUuid,
    )
    localStorage.setItem(
      localStorageKeys.loginUserName,
      loginUserContext.userName,
    )
    localStorage.setItem(
      localStorageKeys.loginUserRole,
      `${loginUserContext.userRole}`,
    )
    localStorage.setItem(
      localStorageKeys.loginCompanyContractPlan,
      `${loginUserContext.contractPlan}`,
    )
    localStorage.setItem(
      localStorageKeys.loginCompanyId,
      `${loginUserContext.companyId}`,
    )
    localStorage.setItem(
      localStorageKeys.loginCompanyName,
      loginUserContext.companyName,
    )
    discardListFiltersOnPlaceNodesChange(loginUserContext.userPlaceNodes)
    localStorage.setItem(
      localStorageKeys.loginUserPlaceNodes,
      JSON.stringify(loginUserContext.userPlaceNodes),
    )
    localStorage.setItem(
      localStorageKeys.loginUserIsRumpRealmAdmin,
      JSON.stringify(loginUserContext.isRumpRealmAdmin ? 1 : 0), // bool 値は1 または 0 で保存する
    )
    localStorage.setItem(
      localStorageKeys.loginUserIsLobsterEnabled,
      JSON.stringify(loginUserContext.isLobsterEnabled ? 1 : 0), // bool 値は1 または 0 で保存する
    )
    localStorage.setItem(
      localStorageKeys.loginUserKioskRollout,
      JSON.stringify(loginUserContext.kioskRollout ? 1 : 0), // bool 値は1 または 0 で保存する
    )

    setUserPropertiesFromLocalStorage()
    invalidateFeatureFlagsQuery(queryClient)

    // eslint-disable-next-line react-hooks/set-state-in-effect -- ローカルストレージ（外部ストア）への書き込み完了を待ってから配下をマウントするために必要
    setStorageReady(true)
  }, [loginUserContext, queryClient])

  const authCheckResult: AuthCheckResult =
    loginUserContext && storageReady ? 'passed' : error ? 'failed' : 'unknown'

  switch (authCheckResult) {
    case 'unknown':
      return (
        <Box sx={{ p: 2 }}>
          <Spinner />
        </Box>
      )
    case 'passed':
      return <Route>{children}</Route>
    case 'failed': {
      // errorCode を Login ページに引き継ぎ、サービス未割り当て時のボタン分岐に利用する
      // ErrorCodeEnum は number のため、truthy 判定だと将来 0 が割り当てられた際にクエリが引き継がれない
      // 可能性があるので != null で明示的に判定する
      const errorCode = error instanceof AuthError ? error.code() : undefined
      const search =
        errorCode != null
          ? `?errorCode=${encodeURIComponent(String(errorCode))}`
          : ''
      const shouldShowDialog = errorDialogInfo !== null && !dialogDismissed
      return shouldShowDialog ? (
        <Dialog open={true}>
          <DialogTitle>{errorDialogInfo?.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{errorDialogInfo?.content}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => setDialogDismissed(true)}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <Redirect to={`/login${search}`} />
      )
    }
  }
}
